// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  type MoveUploadResult,
  uploadMoveData,
  uploadMoveDataResponseSchema,
} from '@/asset/api/upload-move-data';
import { CsvUpload } from '@/asset/components/move-upload/csv-upload';
import { Results } from '@/asset/components/move-upload/results';
import { Stepper } from '@/asset/components/move-upload/stepper';
import { TemplateDownload } from '@/asset/components/move-upload/template-download';
import type {
  ExpandedSectionsState,
  Step,
} from '@/asset/components/move-upload/types';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { useSupportError } from '@/hooks/use-support-error';
import { Box } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface MoveUploadProps {
  onClose: () => void;
}

export function MoveUpload(props: MoveUploadProps) {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<MoveUploadResult | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedSections, setExpandedSections] =
    useState<ExpandedSectionsState>({
      succeeded: false,
      failed: false,
      structureErrors: false,
    });
  const [hasFatalError, setHasFatalError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('data', file);

      const result = await uploadMoveData(formData);
      clearFile();
      setUploadResult(result);
      setCurrentStep('results');
      queryClient.invalidateQueries({ queryKey: ['asset'] });
      queryClient.invalidateQueries({ queryKey: ['movement'] });
    } catch (error) {
      clearFile();
      const axiosError = error as AxiosError;

      // First try to parse as a response with validation errors (rowErrors, structureErrors)
      const knownError = uploadMoveDataResponseSchema.safeParse(
        axiosError.response?.data
      );
      if (knownError.success) {
        // Handled error - show validation results to user
        setUploadResult(knownError.data.data);
        setUploadErrorMessage(null);
        setCurrentStep('results');
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(axiosError);

      // Curated codes from ERROR_MESSAGES are the handled set: show
      // inline, skip Sentry. Anything that falls through to the support
      // fallback is unhandled and must fire Sentry.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while importing moves.');
    } finally {
      setIsUploading(false);
    }
  }, [file, queryClient, showSupportError, uploadLineCount]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedSectionsState) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    setFile(null);
    setUploadResult(null);
    setUploadErrorMessage(null);
    setUploadLineCount(undefined);
    setExpandedSections({
      succeeded: false,
      failed: false,
      structureErrors: false,
    });
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
      setUploadErrorMessage(null);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
      setUploadResult(null);
      setUploadErrorMessage(null);
      clearFile();
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const orgTimezone = user.organization.timezone.name;

  return (
    <Box sx={{ p: 3 }}>
      <Stepper currentStep={currentStep} />

      {currentStep === 'download-template' && (
        <TemplateDownload
          orgTimezone={orgTimezone}
          onBack={onClose}
          onNext={handleNext}
        />
      )}

      {currentStep === 'upload-csv' && (
        <CsvUpload
          uploadErrorMessage={uploadErrorMessage}
          file={file}
          isUploading={isUploading}
          onSelectFile={handleSelectFile}
          onUpload={handleUpload}
          onBack={handleBack}
          onClose={onClose}
          hasFatalError={hasFatalError}
          uploadLineCount={uploadLineCount}
        />
      )}

      {currentStep === 'results' && (
        <Results
          uploadResult={uploadResult}
          expandedSections={expandedSections}
          onToggleExpanded={handleToggleExpanded}
          onBack={handleBack}
          onStartNew={handleStartNew}
          onClose={onClose}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
